<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: '成長記録一覧', href: '/growth-record', color: 'primary' },
          { title: `${className} の成長記録` },
        ]"
      ></v-breadcrumbs>
    </div>
    <page-title :value="`${className} の成長記録`" />
    <div class="my-4">
      <month-pager v-model="yearMonth" @input="pageClick" />
    </div>

    <!-- 異常値メッセージ表示（画面上での注意喚起） -->
    <v-alert v-if="hasAbnormalValue" type="error" density="compact" class="mb-3" title="入力値にエラーがあります">
      入力された数値が大きすぎます。小数点（.）の打ち忘れを確認してください
    </v-alert>

    <div v-if="recordRequests.length > 0" class="py-2">
      <!-- 異常値がある場合はボタンを disabled にする -->
      <v-btn
        color="primary"
        variant="flat"
        :disabled="isSaving || hasAbnormalValue"
        @click="saveRecords"
      >
        保存する
      </v-btn>
    </div>

    <div v-if="isSaving">
      <v-progress-linear indeterminate color="primary" />
    </div>

    <div class="children-table">
      <div class="children-table__headers">
        <div class="children-table__header col-name">園児氏名</div>
        <div class="children-table__header col-height">身長 (cm)</div>
        <div class="children-table__header col-weight">体重 (kg)</div>
        <div class="children-table__header col-other">備考</div>
      </div>
      <class-growth-record-row
        v-for="r in records"
        :key="r.child.childId"
        :record="r"
        @inputHeight="inputHeight($event, r)"
        @inputWeight="inputWeight($event, r)"
        @inputRemarks="inputRemarks($event, r)"
      />
    </div>

    <!-- 異常値保存ブロック用ダイアログ -->
    <v-dialog v-model="showErrorAlert" max-width="450">
      <v-card>
        <v-card-title class="text-h6 text-error font-weight-bold">
          ⚠️ 保存できません
        </v-card-title>
        <v-card-text>
          入力値に異常（許容範囲外の数値）が含まれているため、保存できません。<br />
          正しい数値を入力し直してください。
          <ul class="mt-3 text-caption text-error">
            <li>身長の許容範囲: 30cm 〜 200cm</li>
            <li>体重の許容範囲: 1kg 〜 100kg</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="showErrorAlert = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { classGrowthRecordModule, ChildGrowthRecordRequest } from '@/store/viewModules/growth-record/classGrowthRecordModule';

import PageTitle from '@/components/atoms/PageTitle.vue';
import ClassGrowthRecordRow from '@/components/organisms/growth-record/ClassGrowthRecrodRow.vue';
import MonthPager from '@/components/atoms/MonthPager.vue';
import dayjs from 'dayjs';

import { NurseryGrowthDetailResponse } from 'chaild-api/lib';
import LocalDataService from '@/service/LocalDataService';
import { classModule } from '@/store/dataModules/classModule';

interface DataType {
  isSaving: boolean;
  showErrorAlert: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
    MonthPager,
    ClassGrowthRecordRow,
  },

  data: (): DataType => ({
    isSaving: false,
    showErrorAlert: false,
  }),

  computed: {
    yearMonth: {
      get() {
        return classGrowthRecordModule.yearMonth;
      },
      set(value: string) {
        classGrowthRecordModule.setYearMonth(value);
        classGrowthRecordModule.listGrowthRecords();
      }
    },
    records() {
      return classGrowthRecordModule.growthRecords.filter((r) => {
        if (r.child.quitDate) {
          const now = dayjs();
          const quitDate = dayjs(r.child.quitDate);
          const diff = now.diff(quitDate);
          if (diff > 0) {
            return false;
          }
        }
        return true;
      });
    },
    recordRequests() {
      return classGrowthRecordModule.childGrowthRecordRequests;
    },
    className() {
      if (classGrowthRecordModule.classId) {
        const find = classModule.classes.find((c) => c.classId === classGrowthRecordModule.classId);
        if (find) {
          return find.className;
        }
      }
      return '';
    },
    // 異常値チェック判定プロパティ
    hasAbnormalValue(): boolean {
      return this.recordRequests.some((req) => {
        // 身長チェック（数値に変換して判定）
        if (req.height !== undefined && req.height !== null) {
          const height = Number(req.height);
          // 空文字や数値化できない値を除外し、範囲外（30未満 または 200超）をチェック
          if (!Number.isNaN(height) && String(req.height).trim() !== '') {
            if (height > 130) {
              return true;
            }
          }
        }

        // 体重チェック（数値に変換して判定）
        if (req.weight !== undefined && req.weight !== null) {
          const weight = Number(req.weight);
          // 空文字や数値化できない値を除外し、範囲外（1未満 または 100超）をチェック
          if (!Number.isNaN(weight) && String(req.weight).trim() !== '') {
            if (weight > 50) {
              return true;
            }
          }
        }

        return false;
      });
    },
  },

  methods: {
    async pageClick(pageNumber: string) {
      if (pageNumber) sessionStorage.setItem('classGrowthRecordYearMonth', String(pageNumber));
    },
    inputHeight(value: number, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? { ...find } : { childId: record.child.childId };
      newRecord.height = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    inputWeight(value: number, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? { ...find } : { childId: record.child.childId };
      newRecord.weight = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    inputRemarks(value: string, record: NurseryGrowthDetailResponse) {
      const find = this.recordRequests.find((r) => r.childId === record.child.childId);
      const newRecord: ChildGrowthRecordRequest = find ? { ...find } : { childId: record.child.childId };
      newRecord.remarks = value;

      classGrowthRecordModule.updateChildGrowthRecordRequests(newRecord);
    },
    async saveRecords() {
      // 異常値が存在する場合は保存を行わずにダイアログを出してブロック
      if (this.hasAbnormalValue) {
        this.showErrorAlert = true;
        return;
      }

      this.isSaving = true;
      try {
        await classGrowthRecordModule.saveRecords();
      } finally {
        this.isSaving = false;
      }
    }
  },

  mounted() {
    const classId = this.$route.params.classId as string;
    if (classId) {
      try {
        const classIdInt = parseInt(classId, 10);
        classGrowthRecordModule.setClassId(classIdInt);
        classGrowthRecordModule.listGrowthRecords();
      } catch {
        return;
      }
    }

    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      const classGrowthRecordYearMonth = sessionStorage.getItem('classGrowthRecordYearMonth');
      if (classGrowthRecordYearMonth) this.yearMonth = classGrowthRecordYearMonth;
      classModule.listClass({
        nurseryId: nurseryId,
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.children-table__headers {
  display: flex;
  align-items: center;
  height: 30px;
  border-bottom: solid 1px #e0e0e0;
  margin-bottom: 8px;
}
.children-table__header {
  font-size: 12px;
  font-weight: bold;
}

::v-deep {
  .children-table__row {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 8px 0;
  }
  .children-table__row-col {
    padding: 4px;
    font-size: 14px;
  }
  .col-name { width: 160px; }
  .col-height { width: 120px; }
  .col-weight { width: 120px; }
  .col-other { flex: 1; }
}
</style>